import { Component } from '@theme/component';
import { formatMoney } from '@theme/money-formatting';
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

/**
 * Dagcros product customizer (HANDOFF §7).
 *
 * The section renders the initial state; this component owns the interactive state
 * (colour, view, placements, design mode, text, file, quantity per size), mirrors it into
 * the DOM, persists it per product in sessionStorage, and adds one design as several cart
 * lines (one per size) sharing the same properties (§7.6). The logo file is uploaded once
 * with the first line; the remaining lines reuse the returned file URL.
 * HANDOFF-2 §1: photo thumbnails swap the panel to a product photo and back to the live
 * preview, an optional Notes property goes on every line, and the "Ask on WhatsApp" links
 * carry the current colour and quantity.
 *
 * @typedef {{ x: number, y: number, w: number, h: number, fs: number, r?: number, view: 'front' | 'back' }} Zone
 * @typedef {{ min: number, max: number, price: number | null }} Tier
 * @typedef {{ id: number, available: boolean, price: number }} Variant
 * @typedef {{
 *   productId: number, handle: string, title: string, whatsappNumber: string, garment: string, fromPrice: string,
 *   viewHeight: number, minQty: number, maxQty: number,
 *   maxFileMb: number, moneyFormat: string, currency: string, bulkUrl: string,
 *   initialColour: string, colours: { name: string, hex: string, photo: string | null, photoBack?: string | null }[], sizes: string[],
 *   variants: Record<string, Variant>, tiers: Tier[], zones: Record<string, Zone>,
 *   photoZones: Record<string, Omit<Zone, 'view'>>, embroidered: boolean, embroideryPlacements: string[],
 *   exclusivePlacements?: string[][],
 *   placements: { id: string, name: string, hint: string }[], strings: Record<string, string>
 * }} Config
 * @typedef {{
 *   colour: string, view: 'front' | 'back', placements: string[], mode: 'logo' | 'text',
 *   method: 'print' | 'embroidery', text: string, notes: string, target: number, qty: Record<string, number>
 * }} State
 *
 * @typedef {{
 *   config: HTMLScriptElement, stage: HTMLElement, photo?: HTMLImageElement, zones: HTMLElement[],
 *   caption?: HTMLElement, viewButtons: HTMLButtonElement[],
 *   colourName?: HTMLElement, swatches?: HTMLButtonElement[], sizeRows: HTMLElement[],
 *   qtyValues: HTMLElement[], tiers?: HTMLElement[], statusText: HTMLElement,
 *   bulkNotice: HTMLElement, bulkLink: HTMLAnchorElement,
 *   modeButtons: HTMLButtonElement[], logoPanel: HTMLElement, textPanel: HTMLElement,
 *   dropzone: HTMLElement, dropTitle: HTMLElement, fileInput: HTMLInputElement,
 *   fileError: HTMLElement, removeFile: HTMLButtonElement, textInput: HTMLInputElement,
 *   placeButtons: HTMLButtonElement[], methodGroup?: HTMLElement, methodButtons?: HTMLButtonElement[],
 *   summaryLine: HTMLElement, summaryTotal: HTMLElement,
 *   addButtons: HTMLButtonElement[], reason: HTMLElement, addError: HTMLElement,
 *   barPcs: HTMLElement, barTotal: HTMLElement, liveRegion: HTMLElement,
 *   sizeChart?: HTMLDialogElement, thumbs?: HTMLButtonElement[], thumbArt?: HTMLElement,
 *   galleryImage?: HTMLImageElement, notesInput?: HTMLTextAreaElement, askLinks?: HTMLAnchorElement[],
 *   nudge?: HTMLElement, nudgeText?: HTMLElement, nudgeButton?: HTMLButtonElement, thumbStrip?: HTMLElement,
 *   photoSoon?: HTMLElement, backSoon?: HTMLElement, galleryVideo?: HTMLVideoElement, gallerySound?: HTMLButtonElement,
 *   targetInput?: HTMLInputElement, targetMinus?: HTMLButtonElement, targetPlus?: HTMLButtonElement,
 *   unitPrice?: HTMLElement, grandTotal?: HTMLElement, targetBadge?: HTMLElement, sizesCount?: HTMLElement
 * }} Refs
 *
 * @extends {Component<Refs>}
 */
class DcCustomizerComponent extends Component {
  requiredRefs = ['config', 'stage', 'zones', 'statusText', 'addButtons', 'liveRegion'];

  /** @type {Config} */
  #config;
  /** @type {State} */
  #state;
  /** @type {File | null} */
  #file = null;
  /** @type {string | null} */
  #fileUrl = null;
  #busy = false;
  /** @type {AbortController | undefined} */
  #abortController;
  /** @type {AbortController | undefined} */
  #requestController;
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #announceTimer;
  /** Gallery photo shown in the panel; -1 = the live design preview. */
  #gallery = -1;

  connectedCallback() {
    super.connectedCallback();

    try {
      this.#config = JSON.parse(this.refs.config.textContent ?? '{}');
      // Liquid's `t` filter HTML-escapes ("You&#39;ll"); these strings go into textContent.
      const { strings, placements } = this.#config;
      for (const key of Object.keys(strings)) strings[key] = decodeEntities(strings[key]);
      for (const placement of placements) {
        placement.name = decodeEntities(placement.name);
        placement.hint = decodeEntities(placement.hint);
      }
    } catch (error) {
      console.error('[dc-customizer] invalid config', error);
      return;
    }

    this.#state = this.#initialState();
    this.#abortController = new AbortController();
    this.#bindDropzone(this.#abortController.signal);
    this.#render({ announce: false });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
    this.#requestController?.abort();
    clearTimeout(this.#announceTimer);
    if (this.#fileUrl) URL.revokeObjectURL(this.#fileUrl);
  }

  /* ---------------- State ---------------- */

  get #storageKey() {
    return `dc-customizer:${this.#config.productId}`;
  }

  /** @returns {State} */
  #initialState() {
    const { colours, sizes, placements, initialColour } = this.#config;
    /** @type {State} */
    const state = {
      colour: colours.some((c) => c.name === initialColour) ? initialColour : colours[0]?.name ?? '',
      view: 'front',
      placements: placements[0] ? [placements[0].id] : [],
      mode: 'logo',
      method: 'print',
      text: '',
      notes: '',
      target: this.#config.minQty,
      qty: Object.fromEntries(sizes.map((size) => [size, 0])),
    };

    try {
      const saved = JSON.parse(sessionStorage.getItem(this.#storageKey) ?? 'null');
      if (saved && typeof saved === 'object') {
        if (colours.some((c) => c.name === saved.colour)) state.colour = saved.colour;
        // `placement` (one) is the pre-multi-select format
        const savedPlacements = Array.isArray(saved.placements) ? saved.placements : [saved.placement];
        const valid = placements.filter((p) => savedPlacements.includes(p.id)).map((p) => p.id);
        if (valid.length) state.placements = valid;
        if (saved.mode === 'logo' || saved.mode === 'text') state.mode = saved.mode;
        if (saved.method === 'print' || saved.method === 'embroidery') state.method = saved.method;
        if (typeof saved.text === 'string') state.text = saved.text.slice(0, 40);
        if (typeof saved.notes === 'string') state.notes = saved.notes.slice(0, 500);
        for (const size of sizes) {
          const n = Number(saved.qty?.[size]);
          if (Number.isInteger(n) && n >= 0) state.qty[size] = Math.min(n, 99);
        }
        // Older saves have no target: use the pieces they had
        const assigned = Object.values(state.qty).reduce((sum, n) => sum + n, 0);
        state.target = Number.isInteger(saved.target) ? saved.target : assigned || state.target;
      }
    } catch {
      // Storage can be unavailable (private mode); start fresh.
    }

    state.view = this.#zoneFor(state.placements[0])?.view ?? 'front';

    // "Order one like this" from Our work (HANDOFF-15): ?colour=Navy&method=embroidery
    const params = new URL(window.location.href).searchParams;
    const wantColour = params.get('colour');
    if (wantColour && colours.some((c) => c.name === wantColour)) state.colour = wantColour;
    if (params.get('method') === 'embroidery' && !this.#config.embroidered) {
      const chest = this.#config.embroideryPlacements.find((id) => placements.some((p) => p.id === id));
      if (chest) {
        state.method = 'embroidery';
        if (!state.placements.includes(chest)) state.placements = this.#withPlacement(state.placements, chest);
      }
    } else if (params.get('method') === 'print') {
      state.method = 'print';
    }

    if (new URL(window.location.href).searchParams.get('sample') === '1') {
      state.target = this.#config.minQty;
      state.qty = this.#sampleQuantities();
    }

    state.target = this.#clampTarget(state.target);
    state.qty = this.#fitSizes(state.qty, state.target);
    return state;
  }

  #save() {
    try {
      const { colour, placements, mode, method, text, notes, target, qty } = this.#state;
      sessionStorage.setItem(this.#storageKey, JSON.stringify({ colour, placements, mode, method, text, notes, target, qty }));
    } catch {
      // Ignore quota/private-mode errors.
    }
  }

  /**
   * Sample = the minimum order, one piece per size from M upwards (2 pcs → M 1 + L 1;
   * HANDOFF-8 A3). Starts at the middle size when there's no M; one-size products put every
   * piece on that size.
   */
  #sampleQuantities() {
    const { sizes, minQty } = this.#config;
    const qty = Object.fromEntries(sizes.map((size) => [size, 0]));
    if (sizes.length === 0) return qty;
    const start = sizes.includes('M') ? sizes.indexOf('M') : Math.floor((sizes.length - 1) / 2);
    for (let i = 0; i < minQty; i++) {
      const size = sizes[Math.min(start + i, sizes.length - 1)];
      qty[size] += 1;
    }
    return qty;
  }

  /** Pieces assigned to sizes. */
  get #total() {
    return Object.values(this.#state.qty).reduce((sum, n) => sum + n, 0);
  }

  /** @param {number} n */
  #clampTarget(n) {
    const { minQty, maxQty } = this.#config;
    return Math.max(minQty, Math.min(maxQty, Math.round(Number(n)) || minQty));
  }

  /**
   * Sizes for a total: one-size products put every piece on the single size; otherwise
   * pieces are trimmed from the fullest size until they don't exceed the total.
   * @param {Record<string, number>} qty
   * @param {number} target
   */
  #fitSizes(qty, target) {
    const { sizes } = this.#config;
    if (sizes.length === 1) return { [sizes[0]]: target };
    const next = { ...qty };
    let assigned = Object.values(next).reduce((sum, n) => sum + n, 0);
    while (assigned > target) {
      const fullest = sizes.reduce((a, b) => ((next[b] ?? 0) > (next[a] ?? 0) ? b : a));
      next[fullest] -= 1;
      assigned -= 1;
    }
    return next;
  }

  /** @param {string} size */
  #variantFor(size) {
    return this.#config.variants[`${this.#state.colour}|${size}`];
  }

  /** @param {string} placement */
  #zoneFor(placement) {
    return this.#config.zones[placement];
  }

  /** Selected placements in product order. */
  get #selectedPlacements() {
    return this.#config.placements.filter((p) => this.#state.placements.includes(p.id));
  }

  /** Embroidery is offered only where the product allows it (left chest); otherwise print. */
  get #method() {
    if (this.#config.embroidered) return 'embroidery';
    return this.#state.placements.some((id) => this.#config.embroideryPlacements.includes(id)) ? this.#state.method : 'print';
  }

  /** @param {string} placementId */
  #methodFor(placementId) {
    if (this.#config.embroidered) return 'embroidery';
    return this.#config.embroideryPlacements.includes(placementId) ? this.#method : 'print';
  }

  /** The Method line-item property, e.g. "Print", "Embroidery" or mixed. */
  get #methodLabel() {
    const { strings } = this.#config;
    const selected = this.#selectedPlacements;
    const embroidered = selected.filter((p) => this.#methodFor(p.id) === 'embroidery');
    if (embroidered.length === 0) return strings.methodPrint;
    if (embroidered.length === selected.length) return strings.methodEmbroidery;
    return this.#fill(strings.methodMixed, { placement: embroidered.map((p) => p.name).join(', ') });
  }

  get #hasDesign() {
    return this.#state.mode === 'text' ? this.#state.text.trim() !== '' : this.#file !== null;
  }

  /** "Choose sizes for 3 more pieces…", or '' when every piece has a size. */
  get #sizesMessage() {
    const { strings } = this.#config;
    const remaining = this.#state.target - this.#total;
    if (remaining <= 0) return '';
    return remaining === 1 ? strings.sizesNeedOne : this.#fill(strings.sizesNeed, { count: remaining });
  }

  /** @returns {string} Why the order can't be added yet, or '' when it can. */
  get #reason() {
    const sizes = this.#sizesMessage;
    if (sizes) return sizes;
    if (!this.#hasDesign) return this.#config.strings.reasonDesign;
    return '';
  }

  get #activeTier() {
    const { target } = this.#state;
    return this.#config.tiers.find((tier) => target >= tier.min && target <= tier.max);
  }

  /** First cheaper tier above the current total within the online limit. */
  get #nextTier() {
    const { tiers, maxQty } = this.#config;
    const { target } = this.#state;
    const current = this.#activeTier;
    return tiers
      .filter((tier) => tier.price != null && tier.min > target && tier.min <= maxQty)
      .sort((a, b) => a.min - b.min)
      .find((tier) => current?.price == null || (tier.price ?? 0) < current.price);
  }

  /** Display-only estimate in minor units for the chosen total; real prices come from cart discounts. */
  get #estimate() {
    const tierPrice = this.#activeTier?.price;
    let unit = tierPrice != null ? Math.round(tierPrice * 100) : null;
    if (unit === null) {
      const first = this.#config.sizes.map((size) => this.#variantFor(size)).find(Boolean);
      unit = first?.price ?? 0;
    }
    return { unit, total: unit * this.#state.target };
  }

  /** @param {number} cents */
  #money(cents) {
    // Match Liquid's money_without_trailing_zeros used elsewhere on the page.
    return formatMoney(cents, this.#config.moneyFormat, this.#config.currency).replace(/[.,]00(?!\d)/, '');
  }

  /**
   * @param {string} template
   * @param {Record<string, string | number>} values
   */
  #fill(template, values) {
    return template.replace(/\[(\w+)\]/g, (match, key) => (key in values ? String(values[key]) : match));
  }

  /**
   * wa.me link asking about this product, with the chosen colour and quantity.
   * @param {string} colour
   * @param {number} total
   */
  #askUrl(colour, total) {
    const { strings, title, whatsappNumber } = this.#config;
    const parts = [colour, total > 0 ? this.#fill(total === 1 ? strings.pcsOne : strings.pcs, { count: total }) : '']
      .filter(Boolean)
      .join(', ');
    const message = this.#fill(strings.askMessage, {
      product: title,
      details: parts ? ` (${parts})` : '',
      url: `${window.location.origin}${window.location.pathname}`,
    });
    return `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(message)}`;
  }

  /* ---------------- Rendering ---------------- */

  /** @param {{ announce?: boolean | 'target' }} [options] */
  #render({ announce = true } = {}) {
    const { refs } = this;
    const config = this.#config;
    const state = this.#state;
    const colour = config.colours.find((c) => c.name === state.colour) ?? config.colours[0];
    const selected = this.#selectedPlacements;
    const total = this.#total;

    // Colour
    const hex = colour?.hex ?? '#1B1B1B';
    const dark = isDark(hex);
    this.dataset.view = state.view;
    refs.stage.style.setProperty('--dc-cz-fill', hex);
    // Hood, collar and strap trims sit a shade off the body colour (lighter on dark garments).
    refs.stage.style.setProperty('--dc-cz-trim', dark ? mix(hex, '#FFFFFF', 0.08) : mix(hex, '#000000', 0.05));
    refs.stage.style.setProperty('--dc-cz-detail', dark ? mix(hex, '#FFFFFF', 0.22) : mix(hex, '#000000', 0.2));
    if (refs.thumbArt) {
      refs.thumbArt.style.setProperty('--dc-cz-fill', hex);
      refs.thumbArt.style.setProperty('--dc-cz-trim', refs.stage.style.getPropertyValue('--dc-cz-trim'));
      refs.thumbArt.style.setProperty('--dc-cz-detail', refs.stage.style.getPropertyValue('--dc-cz-detail'));
    }
    this.style.setProperty('--dc-cz-stroke', dark ? 'rgb(255 255 255 / 0.14)' : '#BDB7AA');
    this.style.setProperty('--dc-cz-zone-line', dark ? 'var(--dc-red-line-dk)' : 'var(--dc-red)');
    this.style.setProperty('--dc-cz-zone-ink', dark ? 'var(--dc-paper)' : 'var(--dc-ink)');
    if (refs.colourName) refs.colourName.textContent = colour?.name ?? '';
    for (const swatch of refs.swatches ?? []) {
      swatch.setAttribute('aria-pressed', String(swatch.dataset.colour === state.colour));
    }

    // View + zone. Each view uses the colour's photo for that side when it has one (zones in % of
    // the photo; back photos from HANDOFF-11); otherwise the drawing (zones in viewBox units).
    for (const button of refs.viewButtons ?? []) {
      button.setAttribute('aria-pressed', String(button.dataset.view === state.view));
    }
    const sidePhoto = state.view === 'back' ? colour?.photoBack : colour?.photo;
    const usePhoto = Boolean(sidePhoto) && Boolean(refs.photo);
    this.toggleAttribute('data-photo', usePhoto);
    if (refs.photo) {
      refs.photo.hidden = !usePhoto;
      if (usePhoto && sidePhoto && refs.photo.getAttribute('src') !== sidePhoto) refs.photo.src = sidePhoto;
    }
    // A colour with a front photo but no back photo yet: say so on the back drawing
    if (refs.backSoon) refs.backSoon.hidden = !(state.view === 'back' && colour?.photo && !colour?.photoBack);

    // Every selected placement on the current side gets a zone with the same design.
    const showImage = state.mode === 'logo' && this.#fileUrl !== null;
    const label =
      state.mode === 'text' ? state.text.trim() || config.strings.zoneText : this.#file?.name || config.strings.zoneLogo;
    const viewHeight = config.viewHeight || 210;
    for (const zoneEl of refs.zones ?? []) {
      const id = zoneEl.dataset.placement ?? '';
      const zone = this.#zoneFor(id);
      const photoZone = config.photoZones?.[id];
      const { style } = zoneEl;
      const shown = state.placements.includes(id) && zone !== undefined && zone.view === state.view;
      if (shown && usePhoto && photoZone) {
        style.left = `${photoZone.x}%`;
        style.top = `${photoZone.y}%`;
        style.width = `${photoZone.w}%`;
        style.height = `${photoZone.h}%`;
        style.transform = photoZone.r ? `rotate(${photoZone.r}deg)` : '';
        style.setProperty('--dc-cz-zone-fs', `${photoZone.fs}cqw`);
        zoneEl.hidden = false;
      } else if (shown && !usePhoto && zone) {
        style.left = `${(zone.x / 200) * 100}%`;
        style.top = `${(zone.y / viewHeight) * 100}%`;
        style.width = `${(zone.w / 200) * 100}%`;
        style.height = `${(zone.h / viewHeight) * 100}%`;
        style.transform = zone.r ? `rotate(${zone.r}deg)` : '';
        style.setProperty('--dc-cz-zone-fs', `${zone.fs / 2}cqw`);
        zoneEl.hidden = false;
      } else {
        zoneEl.hidden = true;
        continue;
      }
      zoneEl.dataset.method = this.#methodFor(id);
      const logo = zoneEl.querySelector('.dc-cz__zone-logo');
      const text = zoneEl.querySelector('.dc-cz__zone-label');
      if (logo instanceof HTMLImageElement) {
        logo.hidden = !showImage;
        if (showImage && this.#fileUrl && logo.src !== this.#fileUrl) logo.src = this.#fileUrl;
      }
      if (text instanceof HTMLElement) {
        text.hidden = showImage;
        text.textContent = label;
      }
    }

    if (refs.caption) {
      refs.caption.textContent = this.#fill(config.strings.caption, {
        colour: colour?.name ?? '',
        placement: selected.map((p) => p.name).join(' + '),
      });
    }

    // Gallery: the selected colour's photos plus untagged ones (all when none are tagged), max 4
    if (refs.thumbStrip) {
      const tagged = refs.thumbStrip.dataset.tagged === 'true';
      let shown = 0;
      for (const thumb of refs.thumbs ?? []) {
        const item = thumb.closest('li');
        if (Number(thumb.dataset.index) < 0 || !item) continue;
        const itemColour = item.dataset.colour ?? '';
        const visible = (!tagged || !itemColour || itemColour === state.colour) && shown < 4;
        if (visible) shown += 1;
        item.hidden = !visible;
      }
      refs.thumbStrip.hidden = shown === 0;
      if (refs.photoSoon) {
        const own = (refs.thumbs ?? []).some((t) => Number(t.dataset.index) >= 0 && t.closest('li')?.dataset.colour === state.colour);
        refs.photoSoon.hidden = !tagged || own;
      }
      if (this.#gallery >= 0 && refs.thumbs?.find((t) => Number(t.dataset.index) === this.#gallery)?.closest('li')?.hidden) {
        this.#gallery = -1;
      }
    }

    // Gallery
    this.toggleAttribute('data-gallery', this.#gallery >= 0);
    let videoSrc = '';
    for (const thumb of refs.thumbs ?? []) {
      const index = Number(thumb.dataset.index);
      thumb.setAttribute('aria-pressed', String(index === this.#gallery));
      if (index === this.#gallery && index >= 0 && refs.galleryImage) {
        if (refs.galleryImage.getAttribute('src') !== thumb.dataset.src) refs.galleryImage.src = thumb.dataset.src ?? '';
        refs.galleryImage.alt = thumb.dataset.alt ?? '';
        videoSrc = thumb.dataset.videoSrc ?? '';
      }
    }
    if (refs.galleryImage) refs.galleryImage.hidden = this.#gallery < 0;
    // A video thumb plays muted and looping over the photo (HANDOFF-9); anything else stops it
    const video = refs.galleryVideo;
    if (video) {
      if (videoSrc) {
        if (video.getAttribute('src') !== videoSrc) {
          video.poster = refs.galleryImage?.src ?? '';
          video.src = videoSrc;
        }
        video.hidden = false;
        video.play().catch(() => {});
      } else if (!video.hidden) {
        video.pause();
        video.hidden = true;
      }
      if (refs.gallerySound) refs.gallerySound.hidden = !videoSrc;
    }

    // 02 Quantity: total, price, tier cards, next-tier nudge (HANDOFF-4)
    const { target } = state;
    const { unit, total: estimate } = this.#estimate;
    const totalText = this.#money(estimate);
    const pcsText = this.#fill(target === 1 ? config.strings.pcsOne : config.strings.pcs, { count: target });
    if (refs.targetInput && refs.targetInput.value !== String(target)) refs.targetInput.value = String(target);
    if (refs.targetMinus) refs.targetMinus.disabled = target <= config.minQty;
    if (refs.targetPlus) refs.targetPlus.disabled = target >= config.maxQty;
    if (refs.unitPrice) refs.unitPrice.textContent = this.#money(unit);
    if (refs.grandTotal) refs.grandTotal.textContent = this.#fill(config.strings.grandTotal, { total: totalText });
    if (refs.targetBadge) refs.targetBadge.textContent = pcsText;

    const activeTier = this.#activeTier;
    for (const tierEl of refs.tiers ?? []) {
      const active =
        activeTier !== undefined &&
        Number(tierEl.dataset.min) === activeTier.min &&
        Number(tierEl.dataset.max) === activeTier.max;
      tierEl.setAttribute('aria-pressed', String(active));
    }

    const next = this.#nextTier;
    if (refs.nudge) {
      refs.nudge.hidden = !next;
      if (next && next.price != null && refs.nudgeText && refs.nudgeButton) {
        const add = next.min - target;
        const addText = add === 1 ? config.strings.nudgeAddOne : this.#fill(config.strings.nudgeAdd, { count: add });
        const save = activeTier?.price != null ? this.#money(Math.round((activeTier.price - next.price) * 100)) : '';
        this.#fillRich(refs.nudgeText, config.strings.nudge, {
          add: addText,
          price: this.#money(Math.round(next.price * 100)),
          save,
        }, ['add', 'price']);
        refs.nudgeButton.textContent = this.#fill(config.strings.nudgeButton, { count: next.min });
      }
    }
    if (refs.bulkNotice) refs.bulkNotice.hidden = target < config.maxQty;

    // Sizes: split the total; + stops once every piece has a size
    const full = total >= target;
    for (const row of refs.sizeRows ?? []) {
      const size = row.dataset.size ?? '';
      const variant = this.#variantFor(size);
      const unavailable = !variant || !variant.available;
      const n = state.qty[size] ?? 0;
      const [minus, plus] = row.querySelectorAll('button');
      if (minus) minus.disabled = n === 0;
      if (plus) {
        plus.disabled = unavailable;
        plus.setAttribute('aria-disabled', String(!unavailable && full));
      }
      row.toggleAttribute('data-unavailable', unavailable);
    }
    for (const value of refs.qtyValues ?? []) {
      value.textContent = String(state.qty[value.dataset.size ?? ''] ?? 0);
    }
    if (refs.sizesCount) refs.sizesCount.textContent = this.#fill(config.strings.sizesCount, { assigned: total, target });
    const sizesMessage = this.#sizesMessage;
    const doneMessage = target === 1 ? config.strings.sizesDoneOne : this.#fill(config.strings.sizesDone, { count: target });
    refs.statusText.textContent = sizesMessage || doneMessage;
    refs.statusText.classList.toggle('dc-cz__status-text--need', sizesMessage !== '');
    refs.statusText.classList.toggle('dc-cz__status-text--done', sizesMessage === '');

    // Design mode
    for (const button of refs.modeButtons ?? []) {
      button.setAttribute('aria-pressed', String(button.dataset.mode === state.mode));
    }
    if (refs.logoPanel) refs.logoPanel.hidden = state.mode !== 'logo';
    if (refs.textPanel) refs.textPanel.hidden = state.mode !== 'text';
    if (refs.textInput && refs.textInput.value !== state.text) refs.textInput.value = state.text;
    if (refs.notesInput && refs.notesInput.value !== state.notes) refs.notesInput.value = state.notes;
    if (refs.dropTitle) refs.dropTitle.textContent = this.#file?.name || config.strings.dropTitle;
    if (refs.removeFile) refs.removeFile.hidden = this.#file === null;

    // Placement + method (print or embroidery on the left chest)
    for (const button of refs.placeButtons ?? []) {
      button.setAttribute('aria-pressed', String(state.placements.includes(button.dataset.placement ?? '')));
    }
    // data-method also switches the method note and the approval card wording (CSS).
    const method = this.#method;
    this.dataset.method = method;
    for (const button of refs.methodButtons ?? []) {
      button.setAttribute('aria-pressed', String(button.dataset.method === method));
    }

    // Summary + CTA (the total is always at least the minimum, so never "₹0")
    if (refs.summaryLine) {
      refs.summaryLine.textContent = this.#fill(target === 1 ? config.strings.summaryOne : config.strings.summary, {
        count: target,
        price: this.#money(unit),
      });
    }
    if (refs.summaryTotal) refs.summaryTotal.textContent = this.#fill(config.strings.total, { total: totalText });
    if (refs.barPcs) {
      refs.barPcs.textContent = this.#fill(target === 1 ? config.strings.barPcsOne : config.strings.barPcs, {
        count: target,
        price: this.#money(unit),
      });
    }
    if (refs.barTotal) refs.barTotal.textContent = totalText;

    if (refs.askLinks?.length) {
      const href = this.#askUrl(colour?.name ?? '', target);
      for (const link of refs.askLinks) link.href = href;
    }

    const reason = this.#reason;
    if (refs.reason) refs.reason.textContent = reason;
    for (const button of refs.addButtons ?? []) {
      button.setAttribute('aria-disabled', String(reason !== '' || this.#busy));
    }

    // One live region: a new total announces its price and the sizes line; size changes the sizes line.
    if (announce) {
      const sizesLine = refs.statusText.textContent ?? '';
      const message =
        announce === 'target'
          ? `${this.#fill(config.strings.announceTarget, { pieces: pcsText, price: this.#money(unit), total: totalText })} ${sizesLine}`
          : sizesLine;
      this.#announce(message);
    }
  }

  /**
   * Fills a template into `el`, wrapping the given placeholders in <strong>.
   * @param {HTMLElement} el
   * @param {string} template
   * @param {Record<string, string | number>} values
   * @param {string[]} bold
   */
  #fillRich(el, template, values, bold) {
    const nodes = [];
    for (const part of template.split(/(\[\w+\])/)) {
      const key = part.match(/^\[(\w+)\]$/)?.[1];
      if (key && key in values) {
        if (bold.includes(key)) {
          const strong = document.createElement('strong');
          strong.textContent = String(values[key]);
          nodes.push(strong);
        } else {
          nodes.push(String(values[key]));
        }
      } else if (part) {
        nodes.push(part);
      }
    }
    el.replaceChildren(...nodes);
  }

  /** @param {string} message */
  #announce(message) {
    clearTimeout(this.#announceTimer);
    this.#announceTimer = setTimeout(() => {
      this.refs.liveRegion.textContent = message;
    }, 450);
  }

  /**
   * @param {Partial<State>} patch
   * @param {{ announce?: boolean | 'target' }} [options]
   */
  #update(patch, options) {
    Object.assign(this.#state, patch);
    this.#save();
    this.#render(options);
  }

  /* ---------------- Event handlers (on:* attributes) ---------------- */

  /** @param {'front' | 'back'} view */
  setView(view) {
    if (view !== 'front' && view !== 'back') return;
    this.#gallery = -1;
    this.#update({ view }, { announce: false });
  }

  /** @param {MouseEvent} event */
  setColour(event) {
    const target = event.target instanceof Element ? event.target.closest('[data-colour]') : null;
    if (!(target instanceof HTMLElement)) return;
    const colour = target.dataset.colour ?? '';
    if (!this.#config.colours.some((c) => c.name === colour)) return;

    // Drop quantities for sizes that don't exist or are sold out in the new colour.
    const qty = { ...this.#state.qty };
    for (const size of this.#config.sizes) {
      const variant = this.#config.variants[`${colour}|${size}`];
      if (!variant || !variant.available) qty[size] = 0;
    }
    this.#gallery = -1;
    this.#update({ colour, qty }, { announce: false });
  }

  /** @param {{ size: string, delta: number }} data */
  changeQty(data) {
    const size = String(data?.size ?? '');
    const delta = Number(data?.delta) || 0;
    if (!(size in this.#state.qty)) return;
    const variant = this.#variantFor(size);
    if (delta > 0 && (!variant || !variant.available)) return;
    // Every piece already has a size: raise the total first
    if (delta > 0 && this.#total >= this.#state.target) return;
    const next = Math.max(0, Math.min(99, (this.#state.qty[size] ?? 0) + delta));
    this.#update({ qty: { ...this.#state.qty, [size]: next } });
  }

  applySample() {
    const target = this.#config.minQty;
    this.#update({ target, qty: this.#fitSizes(this.#sampleQuantities(), target) }, { announce: 'target' });
  }

  /**
   * Sets the total pieces; sizes are trimmed (or, for one-size products, filled) to match.
   * @param {string | number} value
   */
  setTarget(value) {
    const target = this.#clampTarget(value);
    this.#update({ target, qty: this.#fitSizes(this.#state.qty, target) }, { announce: 'target' });
  }

  /** @param {string | number} delta */
  changeTarget(delta) {
    this.setTarget(this.#state.target + (Number(delta) || 0));
  }

  /** @param {Event} event */
  handleTarget(event) {
    if (!(event.target instanceof HTMLInputElement)) return;
    this.setTarget(event.target.value);
  }

  goNextTier() {
    const next = this.#nextTier;
    if (next) this.setTarget(next.min);
  }

  /** @param {'logo' | 'text'} mode */
  setMode(mode) {
    if (mode !== 'logo' && mode !== 'text') return;
    this.#gallery = -1;
    this.#update({ mode }, { announce: false });
  }

  /** "No logo file? Add text instead": switch to text and put the cursor in the field. */
  switchToText() {
    this.setMode('text');
    this.refs.textInput?.focus();
  }

  /** Sound on/off for the gallery video. */
  toggleGallerySound() {
    const { galleryVideo: video, gallerySound: button } = this.refs;
    if (!video || !button) return;
    video.muted = !video.muted;
    if (!video.muted && video.paused) video.play().catch(() => {});
    button.setAttribute('aria-pressed', String(!video.muted));
  }

  /**
   * Shows a gallery photo in the panel, or the live preview for -1.
   * @param {string | number} index
   */
  showGallery(index) {
    const next = Number(index);
    const max = (this.refs.thumbs?.length ?? 1) - 2;
    if (!Number.isInteger(next) || next < -1 || next > max) return;
    this.#gallery = next;
    this.#render({ announce: false });
  }

  /**
   * Toggles a placement; at least one always stays selected. Adding one shows its side and
   * drops any placement it overlaps; removing the last one on the current side switches to
   * a side that still has one.
   * @param {string} placement
   */
  setPlacement(placement) {
    if (!this.#config.placements.some((p) => p.id === placement)) return;
    const current = this.#state.placements;
    let placements;
    let view = this.#state.view;
    if (current.includes(placement)) {
      if (current.length === 1) return;
      placements = current.filter((id) => id !== placement);
      if (!placements.some((id) => this.#zoneFor(id)?.view === view)) view = this.#zoneFor(placements[0])?.view ?? view;
    } else {
      placements = this.#withPlacement(current, placement);
      view = this.#zoneFor(placement)?.view ?? view;
    }
    // Embroidery lives on the left chest: without it the design goes back to print.
    const method = placements.some((id) => this.#config.embroideryPlacements.includes(id)) ? this.#state.method : 'print';
    this.#gallery = -1;
    this.#update({ placements, view, method }, { announce: false });
  }

  /**
   * Adds a placement in product order, dropping any it overlaps on the garment
   * (front centre + left chest are either/or).
   * @param {string[]} current
   * @param {string} placement
   */
  #withPlacement(current, placement) {
    const conflicts = (this.#config.exclusivePlacements ?? [])
      .filter((group) => group.includes(placement))
      .flat()
      .filter((id) => id !== placement);
    return this.#config.placements
      .map((p) => p.id)
      .filter((id) => id === placement || (current.includes(id) && !conflicts.includes(id)));
  }

  /**
   * Picking Embroidery adds the left chest (embroidery is left-chest only) and shows the
   * front; other selected placements stay printed (HANDOFF-2 §7, with multi-select).
   * @param {'print' | 'embroidery'} method
   */
  setMethod(method) {
    if (method !== 'print' && method !== 'embroidery') return;
    if (method === 'print') {
      this.#update({ method }, { announce: false });
      return;
    }
    const chest = this.#config.embroideryPlacements.find((id) => this.#config.placements.some((p) => p.id === id));
    if (!chest) return;
    const placements = this.#state.placements.includes(chest)
      ? this.#state.placements
      : this.#withPlacement(this.#state.placements, chest);
    const view = this.#zoneFor(chest)?.view ?? this.#state.view;
    this.#gallery = -1;
    this.#update({ method, placements, view }, { announce: false });
  }

  /** @param {Event} event */
  handleText(event) {
    if (!(event.target instanceof HTMLInputElement)) return;
    this.#gallery = -1;
    this.#update({ text: event.target.value.slice(0, 40) }, { announce: false });
  }

  /** @param {Event} event */
  handleNotes(event) {
    if (!(event.target instanceof HTMLTextAreaElement)) return;
    this.#update({ notes: event.target.value.slice(0, 500) }, { announce: false });
  }

  /** @param {Event} event */
  handleFile(event) {
    if (!(event.target instanceof HTMLInputElement)) return;
    this.#setFile(event.target.files?.[0] ?? null);
  }

  removeFile() {
    this.refs.fileInput.value = '';
    this.#setFile(null);
    this.refs.fileInput.focus();
  }

  /** @param {File | null} file */
  #setFile(file) {
    const { fileError } = this.refs;
    const { maxFileMb, strings } = this.#config;
    fileError.hidden = true;
    fileError.textContent = '';

    if (file) {
      const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
      if (!['png', 'jpg', 'jpeg', 'svg', 'pdf', 'ai', 'cdr'].includes(ext)) {
        this.#showFileError(strings.fileWrongType);
        return;
      }
      if (file.size > maxFileMb * 1024 * 1024) {
        this.#showFileError(strings.fileTooLarge);
        return;
      }
    }

    if (this.#fileUrl) URL.revokeObjectURL(this.#fileUrl);
    this.#file = file;
    this.#gallery = -1;
    // Thumbnails for raster images and SVG (shown through <img>, so scripts never run);
    // PDF, AI and CDR show the file name (HANDOFF-2 §1.4).
    this.#fileUrl = file && /^image\/(png|jpe?g|svg\+xml)$/.test(file.type) ? URL.createObjectURL(file) : null;
    this.#render({ announce: false });
  }

  /** @param {string} message */
  #showFileError(message) {
    const { fileError, fileInput } = this.refs;
    fileInput.value = '';
    fileError.textContent = message;
    fileError.hidden = false;
  }

  /** @param {AbortSignal} signal */
  #bindDropzone(signal) {
    const { dropzone } = this.refs;
    if (!dropzone) return;
    /** @param {DragEvent} event */
    const over = (event) => {
      event.preventDefault();
      dropzone.toggleAttribute('data-dragging', true);
    };
    const leave = () => dropzone.removeAttribute('data-dragging');
    dropzone.addEventListener('dragenter', over, { signal });
    dropzone.addEventListener('dragover', over, { signal });
    dropzone.addEventListener('dragleave', leave, { signal });
    dropzone.addEventListener(
      'drop',
      (event) => {
        event.preventDefault();
        leave();
        const file = event.dataTransfer?.files?.[0];
        if (file) {
          this.#update({ mode: 'logo' }, { announce: false });
          this.#setFile(file);
        }
      },
      { signal }
    );
  }

  openSizeChart() {
    // HANDOFF-2 §3.3: with the "Find your fit" section on the page, go there instead of the dialog.
    const section = document.getElementById('size-chart');
    if (section) {
      for (const row of section.querySelectorAll('[data-size-row]')) {
        if (!(row instanceof HTMLElement)) continue;
        row.toggleAttribute('data-selected', (this.#state.qty[row.dataset.sizeRow ?? ''] ?? 0) > 0);
      }
      const behavior = matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
      section.scrollIntoView({ block: 'start', behavior });
      section.focus({ preventScroll: true });
      return;
    }

    const dialog = this.refs.sizeChart;
    if (!dialog) return;
    let unit = null;
    try {
      unit = localStorage.getItem('dc-size-unit');
    } catch {
      // Storage unavailable: keep the chart's own unit.
    }
    if (unit) this.setSizeUnit(unit);
    // Highlight the sizes already chosen so the customer can check their fit.
    for (const cell of dialog.querySelectorAll('[data-size-col]')) {
      if (!(cell instanceof HTMLElement)) continue;
      cell.toggleAttribute('data-selected', (this.#state.qty[cell.dataset.sizeCol ?? ''] ?? 0) > 0);
    }
    dialog.showModal();
  }

  /** @param {'in' | 'cm' | string} unit */
  setSizeUnit(unit) {
    if (unit !== 'in' && unit !== 'cm') return;
    const guide = this.refs.sizeChart?.querySelector('.dc-sc');
    if (!(guide instanceof HTMLElement)) return;
    guide.dataset.unitActive = unit;
    for (const button of guide.querySelectorAll('[data-unit-button]')) {
      button.setAttribute('aria-pressed', String(button.getAttribute('data-unit-button') === unit));
    }
    try {
      localStorage.setItem('dc-size-unit', unit);
    } catch {
      // Ignore.
    }
  }

  closeSizeChart() {
    this.refs.sizeChart?.close();
  }

  /** @param {MouseEvent} event */
  handleDialogClick(event) {
    if (event.target === this.refs.sizeChart) this.closeSizeChart();
  }

  /* ---------------- Add to cart (HANDOFF §7.6) ---------------- */

  async addToCart() {
    if (this.#busy) return;
    const reason = this.#reason;
    const { refs } = this;
    if (reason) {
      // On phones the reason sits off-screen; take the shopper to the step that needs attention.
      const config = this.#config;
      const target =
        reason === config.strings.reasonDesign
          ? this.#state.mode === 'text'
            ? refs.textInput
            : refs.fileInput
          : (refs.sizeRows ?? []).map((row) => row.querySelector('button:last-of-type:not(:disabled)')).find(Boolean) ??
            refs.sizeRows?.[0];
      const behavior = matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
      const visible = target === refs.fileInput ? refs.dropzone : target;
      visible?.scrollIntoView({ block: 'center', behavior });
      if (target instanceof HTMLElement) target.focus({ preventScroll: true });
      this.#announce(reason);
      return;
    }

    const config = this.#config;
    const state = this.#state;
    const lines = config.sizes
      .filter((size) => (state.qty[size] ?? 0) > 0)
      .map((size) => ({ id: this.#variantFor(size)?.id, quantity: state.qty[size] ?? 0 }))
      .filter((line) => typeof line.id === 'number');
    if (lines.length === 0) return;

    /** @type {Record<string, string>} */
    const properties = {
      Placement: this.#selectedPlacements.map((p) => p.name).join(', '),
      Design: state.mode === 'logo' ? config.strings.designLogo : config.strings.designText,
      Method: this.#methodLabel,
      _design_id: designId(),
      _garment: config.garment,
    };
    if (state.mode === 'text') properties['Print text'] = state.text.trim();
    if (state.notes.trim()) properties.Notes = state.notes.trim();

    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : undefined))
      .filter(Boolean)
      .join(',');

    this.#setBusy(true);
    refs.addError.hidden = true;
    this.#requestController?.abort();
    this.#requestController = new AbortController();
    const { signal } = this.#requestController;

    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: lines.map((line) => ({ merchandiseId: String(line.id), quantity: line.quantity })),
        promise: deferred.promise,
      })
    );

    let didError = false;
    let sections;
    try {
      if (state.mode === 'logo' && this.#file) {
        const [first, ...rest] = lines;
        const firstResponse = await postFormLine(first, properties, this.#file, rest.length ? '' : sectionIds, signal);
        sections = firstResponse.sections;
        if (rest.length) {
          const logoUrl = typeof firstResponse.properties?.Logo === 'string' ? firstResponse.properties.Logo : '';
          const restResponse = await postJsonLines(
            rest.map((line) => ({ ...line, properties: { ...properties, Logo: logoUrl || config.strings.logoSeeFirst } })),
            sectionIds,
            signal
          );
          sections = restResponse.sections;
        }
      } else {
        const response = await postJsonLines(
          lines.map((line) => ({ ...line, properties })),
          sectionIds,
          signal
        );
        sections = response.sections;
      }
    } catch (error) {
      if (signal.aborted) return;
      didError = true;
      const message = error instanceof CartRequestError && error.message ? error.message : config.strings.error;
      refs.addError.textContent = message;
      refs.addError.hidden = false;
      this.dispatchEvent(
        new CartErrorEvent({
          error: message,
          code: error instanceof CartRequestError ? 'INVALID' : 'SERVICE_UNAVAILABLE',
        })
      );
    }

    try {
      const cart = await (await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } })).json();
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'dc-customizer-component',
          sourceId: String(config.productId),
          itemCount: this.#total,
          productId: String(config.productId),
          sections,
          didError,
        },
      });
    } catch (error) {
      deferred.reject(error);
    }

    this.#setBusy(false);
    if (!didError) {
      this.#announce(config.strings.added);
      // A design can be ordered again in other sizes; reset the size split, keep the design and total.
      const cleared = Object.fromEntries(config.sizes.map((size) => [size, 0]));
      this.#update({ qty: this.#fitSizes(cleared, this.#state.target) }, { announce: false });
    } else {
      this.#announce(refs.addError.textContent ?? '');
    }
  }

  /** @param {boolean} busy */
  #setBusy(busy) {
    this.#busy = busy;
    for (const button of this.refs.addButtons ?? []) {
      button.setAttribute('aria-busy', String(busy));
      button.textContent = busy ? this.#config.strings.adding : this.#config.strings.addToCart;
      // #render below restores "Select sizes" when the design was reset to no pieces
    }
    this.#render({ announce: false });
  }
}

class CartRequestError extends Error {}

/**
 * Adds the first line as multipart form data so Shopify stores the logo file.
 * @param {{ id?: number, quantity: number }} line
 * @param {Record<string, string>} properties
 * @param {File} file
 * @param {string} sectionIds
 * @param {AbortSignal} signal
 */
async function postFormLine(line, properties, file, sectionIds, signal) {
  const body = new FormData();
  body.append('id', String(line.id));
  body.append('quantity', String(line.quantity));
  for (const [key, value] of Object.entries(properties)) body.append(`properties[${key}]`, value);
  body.append('properties[Logo]', file, file.name);
  if (sectionIds) body.append('sections', sectionIds);
  return cartRequest(body, { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }, signal);
}

/**
 * @param {{ id?: number, quantity: number, properties: Record<string, string> }[]} items
 * @param {string} sectionIds
 * @param {AbortSignal} signal
 */
async function postJsonLines(items, sectionIds, signal) {
  const payload = { items, ...(sectionIds ? { sections: sectionIds } : {}) };
  return cartRequest(
    JSON.stringify(payload),
    { 'Content-Type': 'application/json', Accept: 'application/json' },
    signal
  );
}

/**
 * @param {BodyInit} body
 * @param {Record<string, string>} headers
 * @param {AbortSignal} signal
 */
async function cartRequest(body, headers, signal) {
  const response = await fetch(Theme.routes.cart_add_url, { method: 'POST', headers, body, signal });
  const json = await response.json();
  if (!response.ok || json.status) {
    throw new CartRequestError(json.description || json.message || '');
  }
  return json;
}

function designId() {
  if (globalThis.crypto?.randomUUID) return crypto.randomUUID().slice(0, 13);
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

/** @param {string} value */
function decodeEntities(value) {
  if (!value || !value.includes('&')) return value;
  return new DOMParser().parseFromString(value, 'text/html').documentElement.textContent ?? value;
}

/** @param {string} hex */
function toRgb(hex) {
  const value = hex.trim().replace('#', '');
  const full = value.length === 3 ? value.replace(/./g, (c) => c + c) : value;
  return [0, 2, 4].map((i) => parseInt(full.slice(i, i + 2), 16));
}

/**
 * Mixes two hex colours; amount 0 keeps `a`, 1 returns `b`.
 * @param {string} a
 * @param {string} b
 * @param {number} amount
 */
function mix(a, b, amount) {
  const [r1, g1, b1] = toRgb(a);
  const [r2, g2, b2] = toRgb(b);
  const channel = (x, y) => Math.round(x + (y - x) * amount).toString(16).padStart(2, '0');
  return `#${channel(r1, r2)}${channel(g1, g2)}${channel(b1, b2)}`;
}

/** @param {string} hex */
function isDark(hex) {
  const value = hex.trim().replace('#', '');
  const full = value.length === 3 ? value.replace(/./g, (c) => c + c) : value;
  const r = parseInt(full.slice(0, 2), 16);
  const g = parseInt(full.slice(2, 4), 16);
  const b = parseInt(full.slice(4, 6), 16);
  return (r * 299 + g * 587 + b * 114) / 1000 < 150;
}

if (!customElements.get('dc-customizer-component')) {
  customElements.define('dc-customizer-component', DcCustomizerComponent);
}
